import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
    Search, X, ArrowRight, User, FileText, LayoutGrid, Zap, RefreshCw,
    AlertTriangle, Ticket, Users, Building, MessageSquare, Shield, FolderOpen,
    Calculator, CheckSquare, ArrowDownUp, History, Sparkles, ExternalLink,
    Building2, Plane, Briefcase, Plus,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { resolveCrmEntityRoute } from '../../utils/crmRouteResolver';
import { resolvePnrRoute } from '../../utils/pnrRouteResolver';
import {
    buildMasterSearchEntityUrl,
    MASTER_SEARCH_CATEGORY_TYPES,
    quotePostgrestIlikePattern,
    type MasterSearchCategory,
    type MasterSearchResultType,
} from '../../utils/masterSearch';
import {
    areNavigationPathsAllowed,
    buildNavigationPageSearchResults,
    getNavigationPermissionPath,
    getAllowedEntitySearchTypes,
    rankNavigationCandidates,
    type NavigationPageSourceSection,
} from '../../utils/navigationSearch';
import {
    addNavigationHistoryItem,
    clearNavigationHistory,
    loadNavigationHistory,
    type StoredNavigationHistoryItem,
} from '../../utils/navigationHistory';
import {
    getNavigationPermissionScopeKey,
    getVisibleNavigationScopedItems,
    type NavigationScopedItems,
} from '../../utils/navigationPermissionState';
import { usePrivacyMode } from '../../hooks/usePrivacyMode';
import { maskMasterSearchResult } from '../../utils/masterSearchPrivacy';
import { freshdeskMarkLabel } from '../Communications/freshdeskMirrorMarks';
import { findCustomers, type CustomerSearchRow } from '../../services/crmFindCustomersService';
import { crmSearch, type CrmSearchResult } from '../../services/crmSearchService';
import { customerSearchDisplay, isExactCustomerMatch } from './masterSearchCustomers';
import { useCommandContext } from '../../hooks/useCommandContext';

interface SearchResult {
    id: string;
    type: MasterSearchResultType;
    title: string;
    subtitle?: string;
    icon: React.ElementType;
    url: string;
    domain?: string;
    keywords?: readonly string[];
    permissionPaths?: readonly string[];
    /** Set for customers found through the Customers page search; labels are built from it at render time. */
    customerRow?: CustomerSearchRow;
}

interface MasterSearchPaletteProps {
    isOpen?: boolean;
    onClose?: () => void;
    /** Controlled hosts: called when the shared Ctrl/Cmd+K handler asks this palette to open. */
    onOpen?: () => void;
    navigationSections: NavigationPageSourceSection<React.ElementType>[];
    allowedPaths: ReadonlySet<string>;
}

interface SearchAction extends SearchResult {
    permissionPaths: readonly string[];
}

interface PersonSearchRow {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
}

interface CompanySearchRow {
    id: string;
    company_name: string | null;
    relationship_type: string | null;
}

interface PntSearchRow {
    id: string;
    passenger_name: string | null;
    pnr_locator: string | null;
}

interface InvoiceSearchRow {
    id: string;
    invoice_number: string | null;
    total_amount: number | null;
    currency: string | null;
}

interface SupplierSearchRow {
    id: string;
    supplier_name: string | null;
    supplier_code: string | null;
}

interface PnrSearchRow {
    id: string;
    record_locator: string | null;
    passenger_name: string | null;
    gds_source: string | null;
}

interface TicketSearchRow {
    id: string;
    display_id: number;
    subject: string | null;
    status_name: string | null;
    source_type: string | null;
    freshdesk_ticket_id: number | null;
    deleted?: boolean | null;
    spam?: boolean | null;
}

interface TripSearchRow {
    id: string;
    trip_name: string | null;
    primary_destination: string | null;
    trip_status: string | null;
}

interface DocumentSearchRow {
    id: string;
    document_name: string | null;
    document_number: string | null;
    document_type: string | null;
}

interface TaskSearchRow {
    id: string;
    title: string | null;
    status: string | null;
    priority: string | null;
}

interface SearchQueryResponse {
    data: unknown[] | null;
    error?: unknown;
}

function personSearchTitle(person: PersonSearchRow, fallback: string): string {
    return [person.first_name, person.last_name].filter(Boolean).join(' ') || fallback;
}

const categoryConfig: { id: MasterSearchCategory; label: string; icon: React.ElementType }[] = [
    { id: 'all', label: 'All', icon: Search },
    { id: 'context', label: 'Context', icon: Zap },
    { id: 'pages', label: 'Pages', icon: LayoutGrid },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'companies', label: 'Companies', icon: Building },
    { id: 'bookings', label: 'Bookings (PNT)', icon: Ticket },
    { id: 'pnrs', label: 'PNRs', icon: FileText },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'travelers', label: 'Travelers', icon: User },
    { id: 'suppliers', label: 'Suppliers', icon: Building2 },
    { id: 'trips', label: 'Trips', icon: Plane },
    { id: 'documents', label: 'Documents', icon: FolderOpen },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare },
    { id: 'tickets', label: 'UCC Tickets', icon: MessageSquare },
];

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const typeColors: Record<string, string> = {
    feature: 'bg-slate-100 text-slate-600',
    context: 'bg-indigo-100 text-indigo-600',
    customer: 'bg-blue-100 text-blue-600',
    company: 'bg-purple-100 text-purple-600',
    traveler: 'bg-cyan-100 text-cyan-600',
    booking: 'bg-amber-100 text-amber-600',
    pnr: 'bg-orange-100 text-orange-600',
    invoice: 'bg-green-100 text-green-600',
    pnt: 'bg-amber-100 text-amber-600',
    supplier: 'bg-indigo-100 text-indigo-600',
    trip: 'bg-sky-100 text-sky-700',
    document: 'bg-teal-100 text-teal-700',
    task: 'bg-rose-100 text-rose-700',
    ticket: 'bg-blue-100 text-blue-600',
};

const GLOBAL_SEARCH_ACTIONS: SearchAction[] = [
    { id: 'action-new-pnr', type: 'context', title: 'Create New PNR', subtitle: 'Start a booking file', icon: Plus, url: '/pnr/new', domain: 'Booking Actions', permissionPaths: ['/pnr', '/pnr/new'], keywords: ['new booking', 'record locator'] },
    { id: 'action-new-customer', type: 'context', title: 'Add Customer', subtitle: 'Create a CRM customer', icon: User, url: '/crm?openAdd=1', domain: 'CRM Actions', permissionPaths: ['/crm', '/crm/customers'], keywords: ['new client', 'create customer'] },
    { id: 'action-pnt-urgent', type: 'context', title: 'Show Urgent PNT', subtitle: 'Open critical paid-not-ticketed work', icon: AlertTriangle, url: '/pnt?qf=critical', domain: 'Ticketing Actions', permissionPaths: ['/pnt'], keywords: ['paid not ticketed', 'deadline', 'urgent ticketing'] },
    { id: 'action-queue-ingest', type: 'context', title: 'Ingest High Priority Queues', subtitle: 'Open queue ingestion controls', icon: RefreshCw, url: '/queue-center?action=ingest', domain: 'Queue Actions', permissionPaths: ['/queue-center'], keywords: ['sabre queue', 'amadeus queue', 'gds queue'] },
    { id: 'action-queue-amadeus', type: 'context', title: 'Open Amadeus Queues', subtitle: 'Switch Queue Command Center to Amadeus', icon: ArrowDownUp, url: '/queue-center?gds=amadeus', domain: 'Queue Actions', permissionPaths: ['/queue-center'], keywords: ['gds', 'queue monitor'] },
    { id: 'action-rtw-pricing', type: 'context', title: 'Run RTW Pricing Analysis', subtitle: 'Open RTW price and optimise', icon: Calculator, url: '/rtw?action=pricing', domain: 'RTW Actions', permissionPaths: ['/rtw'], keywords: ['round the world', 'quote', 'fare'] },
    { id: 'action-rtw-assistant', type: 'context', title: 'Open RTW AI Assistant', subtitle: 'Research and analyse an RTW itinerary', icon: Sparkles, url: '/rtw/assistant', domain: 'RTW Actions', permissionPaths: ['/rtw', '/rtw/assistant'], keywords: ['round the world', 'gds assistant', 'route research'] },
    { id: 'action-supplier-invoices', type: 'context', title: 'Review Supplier Invoices', subtitle: 'Inspect ingested invoice PDFs and parsing', icon: FileText, url: '/finance/supplier-invoices', domain: 'Finance Actions', permissionPaths: ['/finance/supplier-invoices'], keywords: ['gmail invoice', 'supplier document', 'pdf parsing'] },
    { id: 'action-system-health', type: 'context', title: 'Open System Health', subtitle: 'Inspect service, OpenRouter, Fora, and ingestion status', icon: Shield, url: '/admin/system-health', domain: 'Admin Actions', permissionPaths: ['/admin/system-health'], keywords: ['openrouter', 'fora stale', 'status header', 'service monitoring'] },
];

const crmSlashCommands: Array<SearchAction & { keywords: string[] }> = [
    { id: 'crm-home-cmd', type: 'feature', title: '/crm', subtitle: 'Open CRM Home', icon: Users, url: '/crm', domain: 'CRM', permissionPaths: ['/crm'], keywords: ['home', 'customers'] },
    { id: 'crm-add-cmd', type: 'feature', title: '/crm add', subtitle: 'Create a new customer', icon: Plus, url: '/crm?openAdd=1', domain: 'CRM', permissionPaths: ['/crm', '/crm/customers'], keywords: ['new', 'create'] },
];

function dedupeSearchResults(results: SearchResult[]): SearchResult[] {
    const seen = new Set<string>();
    return results.filter((result) => {
        const key = `${result.type}:${result.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/** crm_search_parties reasons that identify one traveller: these go first, like an exact customer match. */
const TRAVELLER_EXACT_MATCH_LABELS: Readonly<Record<string, string>> = {
    phone_exact: 'Phone match',
    id_exact: 'ID match',
    email_exact: 'E-mail match',
};

/**
 * Travellers found by phone or ID, and the customer a matching traveller belongs to, from crm_search_parties (the
 * search the earlier CRM palette used). Travellers are otherwise found by name and e-mail only. A customer found
 * through a traveller opens the customer page, so it is offered only with Customers access as well.
 */
function partySearchResults(parties: readonly CrmSearchResult[], canSeeCustomers: boolean): { exactTravellers: SearchResult[]; viaTraveller: SearchResult[] } {
    const exactTravellers: SearchResult[] = [];
    const viaTraveller: SearchResult[] = [];
    for (const party of parties) {
        const exactLabel = TRAVELLER_EXACT_MATCH_LABELS[party.reason];
        if (party.entityType === 'traveler' && exactLabel) {
            exactTravellers.push({ id: `t-${party.id}`, type: 'traveler', title: party.displayName || 'Unnamed traveler', subtitle: `Traveler · ${exactLabel}`, icon: User, url: resolveCrmEntityRoute('traveler', party.id) });
        } else if (party.entityType === 'customer_via_traveler' && canSeeCustomers && viaTraveller.length < 3) {
            const customerId = party.linkedCustomerId ?? party.id;
            viaTraveller.push({ id: `c-${customerId}`, type: 'customer', title: party.displayName || 'Unnamed customer', subtitle: 'Customer · via a matching traveler', icon: User, url: resolveCrmEntityRoute('customer', customerId) });
        }
    }
    return { exactTravellers, viaTraveller };
}

function getResultPermissionPaths(result: SearchResult): readonly string[] {
    if (result.permissionPaths) return result.permissionPaths;
    const entityPaths: Partial<Record<MasterSearchResultType, readonly string[]>> = {
        customer: ['/crm/customers'],
        company: ['/crm/companies'],
        traveler: ['/crm/travelers'],
        booking: ['/pnt'],
        pnt: ['/pnt'],
        pnr: ['/pnr'],
        invoice: ['/invoices'],
        supplier: ['/crm/suppliers'],
        trip: ['/trips'],
        document: ['/crm/documents'],
        task: ['/tasks'],
        ticket: ['/ucc'],
    };
    return entityPaths[result.type] ?? [getNavigationPermissionPath(result.url)];
}

export function MasterSearchPalette({ isOpen: propIsOpen, onClose, onOpen, navigationSections, allowedPaths }: MasterSearchPaletteProps) {
    const { user } = useAuth();
    const privacy = usePrivacyMode();
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = propIsOpen !== undefined ? propIsOpen : internalOpen;
    
    const [query, setQuery] = useState('');
    const permissionScopeKey = useMemo(
        () => getNavigationPermissionScopeKey(user?.id ?? null, allowedPaths),
        [allowedPaths, user?.id],
    );
    const [resultState, setResultState] = useState<NavigationScopedItems<SearchResult>>({
        scopeKey: '',
        items: [],
    });
    const results = getVisibleNavigationScopedItems(resultState, permissionScopeKey);
    // The (trimmed) query the shown results belong to. Until it equals the typed query and no record search is
    // running, the results on screen are stale and Enter waits for the fresh ones.
    const [resultsQuery, setResultsQuery] = useState('');
    const setScopedResults = useCallback((items: SearchResult[], forQuery = '') => {
        setResultState({ scopeKey: permissionScopeKey, items });
        setResultsQuery(forQuery);
    }, [permissionScopeKey]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [activeCategory, setActiveCategory] = useState<MasterSearchCategory>('all');
    const [searchHistory, setSearchHistory] = useState<{
        userId: string | null;
        items: StoredNavigationHistoryItem[];
    }>({ userId: null, items: [] });
    const [isSearching, setIsSearching] = useState(false);
    
    const navigate = useNavigate();
    const location = useLocation();
    const inputRef = useRef<HTMLInputElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const previousPrivacyRef = useRef(privacy);

    useEffect(() => {
        if (privacy && !previousPrivacyRef.current) {
            // A populated palette can contain a raw customer name in both the input and
            // cached results. Rendering below masks them immediately; this clears the
            // retained state after the transition as well.
            setQuery('');
            setScopedResults([]);
            setSelectedIndex(0);
        }
        previousPrivacyRef.current = privacy;
    }, [privacy, setScopedResults]);

    const navigationFeatures = useMemo<SearchResult[]>(
        () => buildNavigationPageSearchResults(navigationSections),
        [navigationSections],
    );

    const allowedEntitySearchTypes = useMemo(
        () => getAllowedEntitySearchTypes(allowedPaths),
        [allowedPaths],
    );
    const allowedGlobalActions = useMemo(
        () => GLOBAL_SEARCH_ACTIONS.filter((action) => (
            areNavigationPathsAllowed(allowedPaths, action.permissionPaths)
        )),
        [allowedPaths],
    );
    const visibleSearchHistoryItems = useMemo(() => (
        searchHistory.userId === (user?.id ?? null)
            ? searchHistory.items.filter((item) => (
                areNavigationPathsAllowed(allowedPaths, item.permissionPaths)
            ))
            : []
    ), [allowedPaths, searchHistory, user?.id]);

    const handleClose = useCallback(() => {
        if (onClose) onClose();
        if (propIsOpen === undefined) setInternalOpen(false);
    }, [onClose, propIsOpen]);

    // Ctrl/Cmd+K and Escape go through the shared command-context registry so one keypress opens one surface only
    // (a record palette on the page wins; "Search everywhere" switches to this one) and Escape closes only the active surface.
    useCommandContext({
        id: 'master-search',
        scope: 'global',
        open: isOpen,
        onOpenChange: (next) => {
            if (next) {
                if (propIsOpen === undefined) setInternalOpen(true);
                else onOpen?.();
            } else {
                handleClose();
            }
        },
    });

    // Focus input, contain keyboard focus, and restore the launch point on close.
    useEffect(() => {
        if (!isOpen) {
            setQuery('');
            setScopedResults([]);
            setActiveCategory('all');
            setSelectedIndex(0);
            return;
        }

        previouslyFocusedRef.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        const userId = user?.id ?? null;
        let history: StoredNavigationHistoryItem[] = [];
        try {
            history = loadNavigationHistory(localStorage, userId, allowedPaths);
        } catch { /* localStorage unavailable — non-fatal */ }
        setSearchHistory({ userId, items: history });
        const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 50);

        const containFocus = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;
            const dialog = dialogRef.current;
            if (!dialog) return;
            const focusableElements = Array.from(
                dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
            ).filter((element) => element.offsetParent !== null);
            if (focusableElements.length === 0) {
                event.preventDefault();
                inputRef.current?.focus();
                return;
            }

            const first = focusableElements[0];
            const last = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement;
            if (!dialog.contains(activeElement)) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            } else if (event.shiftKey && activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', containFocus, true);
        return () => {
            window.clearTimeout(focusTimer);
            document.removeEventListener('keydown', containFocus, true);
            const previous = previouslyFocusedRef.current;
            if (previous?.isConnected) previous.focus();
            previouslyFocusedRef.current = null;
        };
    }, [allowedPaths, isOpen, setScopedResults, user?.id]);

    // Construct contextual commands from the same permission-filtered action index.
    const contextCommands = useMemo(() => {
        const commands: SearchResult[] = [];
        const path = location.pathname;

        const addAllowedAction = (actionId: string) => {
            const action = allowedGlobalActions.find((candidate) => candidate.id === actionId);
            if (action) commands.push(action);
        };

        const addDynamicCommand = (command: SearchResult, permissionPaths: readonly string[]) => {
            if (areNavigationPathsAllowed(allowedPaths, permissionPaths)) {
                commands.push({ ...command, permissionPaths });
            }
        };

        if (path.startsWith('/crm/customers/')) {
            const customerId = path.split('/').pop();
            addDynamicCommand({ id: 'ctx-book', type: 'context', title: 'Create Booking for this Customer', icon: Ticket, url: `/pnr/new?customer=${customerId}`, domain: 'Context Actions' }, ['/pnr', '/pnr/new']);
            addDynamicCommand({ id: 'ctx-msg', type: 'context', title: 'Message this Customer', icon: MessageSquare, url: `/ucc?customer=${customerId}`, domain: 'Context Actions' }, ['/ucc']);
            addDynamicCommand({ id: 'ctx-inv', type: 'context', title: 'View Customer Invoices', icon: FileText, url: `/invoices?customer=${customerId}`, domain: 'Context Actions' }, ['/invoices']);
        } else if (path.startsWith('/pnr/record/') || /^\/pnr\/[a-z0-9]{2}-[a-z0-9]+$/i.test(path)) {
            const pnrSegment = path.split('/').pop();
            addDynamicCommand({ id: 'ctx-invoice-pnr', type: 'context', title: 'Create Invoice for this PNR', icon: FileText, url: `/invoices/new?pnr=${pnrSegment}`, domain: 'Context Actions' }, ['/invoices', '/invoices/new']);
            addDynamicCommand({ id: 'ctx-ticket-pnr', type: 'context', title: 'Add to PNT Board', icon: Briefcase, url: `/pnt?pnr=${pnrSegment}`, domain: 'Context Actions' }, ['/pnt']);
        } else if (path.startsWith('/queue-center')) {
            addAllowedAction('action-queue-ingest');
            addAllowedAction('action-queue-amadeus');
        } else if (path.startsWith('/rtw')) {
            addAllowedAction('action-rtw-pricing');
            addAllowedAction('action-rtw-assistant');
            addDynamicCommand({ id: 'ctx-rtw-customer', type: 'context', title: 'Load Customer Profile', icon: User, url: '/crm?openSearch=true', domain: 'Context Actions' }, ['/crm', '/crm/customers']);
        } else if (path === '/') {
            addAllowedAction('action-new-pnr');
            addAllowedAction('action-new-customer');
            addAllowedAction('action-pnt-urgent');
        }
        
        return commands;
    }, [allowedGlobalActions, allowedPaths, location.pathname]);

    // The core search engine
    useEffect(() => {
        const controller = new AbortController();
        let isActive = true;

        const search = async () => {
            if (query.trim().length === 0) {
                // If empty, just show context commands
                setScopedResults(contextCommands, '');
                setIsSearching(false);
                return;
            }

            setIsSearching(true);
            const searchTerm = query.trim().slice(0, 120);
            const searchLower = searchTerm.toLowerCase();
            const searchPattern = quotePostgrestIlikePattern(searchTerm);
            const rankResults = (items: readonly SearchResult[]) => rankNavigationCandidates(
                searchTerm,
                items.map((item) => ({
                    item,
                    label: item.title,
                    keywords: [item.subtitle ?? '', item.domain ?? '', ...(item.keywords ?? [])],
                })),
            );
            const newResults: SearchResult[] = [
                ...rankResults(contextCommands),
                ...rankResults(allowedGlobalActions),
                ...rankResults(navigationFeatures),
            ];

            if (searchLower.startsWith('/crm')) {
                const suffix = searchLower.replace('/crm', '').trim();
                crmSlashCommands.forEach((command) => {
                    const haystack = `${command.title} ${command.subtitle || ''} ${command.keywords.join(' ')}`.toLowerCase();
                    if (areNavigationPathsAllowed(allowedPaths, command.permissionPaths)
                        && (!suffix || haystack.includes(suffix))) newResults.push(command);
                });
            }

            // Page and action matches are local and should never wait for record I/O.
            // This also prevents a transient false "No matches" state on slower links.
            if (isActive) {
                setScopedResults(dedupeSearchResults(newResults), searchTerm);
                setSelectedIndex(0);
            }

            if (searchTerm.length > 1) {
                try {
                    const emptyResult = () => Promise.resolve({ data: [] as unknown[], error: null });
                    // Customers: the Customers page search (crm_find_customers) finds by customer number, booking
                    // code / PNR, phone in any format, e-mail and name, as the signed-in staff member (the database
                    // applies can_access_customer). Only if that search is unavailable does the header fall back to
                    // the earlier name / e-mail lookup, which RLS also scopes.
                    const customerLookup: Promise<CustomerSearchRow[] | null> = allowedEntitySearchTypes.has('customers')
                        ? findCustomers({ query: searchTerm, pageSize: 6 }).then((page) => page.rows, () => null)
                        : Promise.resolve([]);
                    // Travellers by phone or ID and customers via a traveller (crm_search_parties), for staff with
                    // traveller access only. A failed search adds nothing; the other lookups still answer.
                    const partyLookup: Promise<CrmSearchResult[]> = allowedEntitySearchTypes.has('travelers')
                        ? crmSearch(searchTerm, { limit: 8, enableFreshdeskFallback: false }).then((response) => response.local, () => [])
                        : Promise.resolve([]);
                    const promises = [
                        // Customers (fallback only; skipped unless the directory search fails)
                        customerLookup.then(async (rows): Promise<SearchQueryResponse> => (
                            rows === null
                                ? await supabase.from('customers').select('id, first_name, last_name, email')
                                    .or(`first_name.ilike.${searchPattern},last_name.ilike.${searchPattern},email.ilike.${searchPattern}`).limit(5).abortSignal(controller.signal)
                                : emptyResult()
                        )),
                        // Companies
                        allowedEntitySearchTypes.has('companies')
                            ? supabase.from('companies').select('id, company_name, relationship_type')
                                .is('archived_at', null).ilike('company_name', `%${searchTerm}%`).limit(3).abortSignal(controller.signal)
                            : emptyResult(),
                        // PNTs
                        allowedEntitySearchTypes.has('pnt')
                            ? supabase.from('pnt_transactions').select('id, passenger_name, pnr_locator, service_provider_name')
                                .or(`passenger_name.ilike.${searchPattern},pnr_locator.ilike.${searchPattern}`).limit(5).abortSignal(controller.signal)
                            : emptyResult(),
                        // Invoices
                        allowedEntitySearchTypes.has('invoices')
                            ? supabase.from('invoices').select('id, invoice_number, total_amount, currency')
                                .ilike('invoice_number', `%${searchTerm}%`).limit(3).abortSignal(controller.signal)
                            : emptyResult(),
                        // Suppliers
                        allowedEntitySearchTypes.has('suppliers')
                            ? supabase.from('suppliers').select('id, supplier_name, supplier_code')
                                .or(`supplier_name.ilike.${searchPattern},supplier_code.ilike.${searchPattern}`).limit(3).abortSignal(controller.signal)
                            : emptyResult(),
                        // Travelers
                        allowedEntitySearchTypes.has('travelers')
                            ? supabase.from('travelers').select('id, first_name, last_name, email')
                                .or(`first_name.ilike.${searchPattern},last_name.ilike.${searchPattern},email.ilike.${searchPattern}`).is('archived_at', null).limit(3).abortSignal(controller.signal)
                            : emptyResult(),
                        // PNRs - Direct lookup into PNR Records!
                        allowedEntitySearchTypes.has('pnrs')
                            ? supabase.from('pnr_records').select('id, record_locator, passenger_name, gds_source')
                                .or(`record_locator.ilike.${searchPattern},passenger_name.ilike.${searchPattern}`).limit(5).abortSignal(controller.signal)
                            : emptyResult(),
                        // UCC Tickets — by display_id (numeric) or subject/description.
                        // A conversation Freshdesk deleted or marked spam is still found (somebody may look it up by
                        // number), but labelled as such in place of its status.
                        // Match on display_id only when the query is numeric to keep it fast.
                        !allowedEntitySearchTypes.has('tickets')
                            ? emptyResult()
                            : /^\d+$/.test(searchTerm)
                            ? supabase.from('comm_tickets')
                                .select('id, display_id, subject, status_name, source_type, freshdesk_ticket_id, deleted, spam')
                                .eq('display_id', parseInt(searchTerm, 10))
                                .limit(5).abortSignal(controller.signal)
                            : supabase.from('comm_tickets')
                                .select('id, display_id, subject, status_name, source_type, freshdesk_ticket_id, deleted, spam')
                                .ilike('subject', `%${searchTerm}%`)
                                .order('updated_at', { ascending: false })
                                .limit(5).abortSignal(controller.signal),
                        // Trips
                        allowedEntitySearchTypes.has('trips')
                            ? supabase.from('trips').select('id, trip_name, primary_destination, start_date, end_date, trip_status, super_pnr')
                                .or(`trip_name.ilike.${searchPattern},primary_destination.ilike.${searchPattern},super_pnr.ilike.${searchPattern}`).limit(5).abortSignal(controller.signal)
                            : emptyResult(),
                        // Canonical customer document vault
                        allowedEntitySearchTypes.has('documents')
                            ? supabase.from('document_vault').select('id, document_name, document_number, document_type, expiry_date, customer_id')
                                .or(`document_name.ilike.${searchPattern},document_number.ilike.${searchPattern}`).limit(5).abortSignal(controller.signal)
                            : emptyResult(),
                        // Unified operational tasks
                        allowedEntitySearchTypes.has('tasks')
                            ? supabase.from('tasks').select('id, title, status, priority, due_date')
                                .or(`title.ilike.${searchPattern},description.ilike.${searchPattern}`).limit(5).abortSignal(controller.signal)
                            : emptyResult(),
                    ];

                    const [directoryCustomers, dbResults, parties] = await Promise.all([
                        customerLookup,
                        Promise.allSettled(promises),
                        partyLookup,
                    ]);
                    if (!isActive) return;
                    const getData = <T,>(index: number): T[] => {
                        const result = dbResults[index];
                        if (result.status !== 'fulfilled') return [];
                        const response = result.value as SearchQueryResponse;
                        if (response.error || !response.data) return [];
                        return response.data as T[];
                    };

                    const customers = getData<PersonSearchRow>(0);
                    const companies = getData<CompanySearchRow>(1);
                    const pnts = getData<PntSearchRow>(2);
                    const invoices = getData<InvoiceSearchRow>(3);
                    const suppliersData = getData<SupplierSearchRow>(4);
                    const travelers = getData<PersonSearchRow>(5);
                    const pnrs = getData<PnrSearchRow>(6);
                    const tickets = getData<TicketSearchRow>(7);
                    const trips = getData<TripSearchRow>(8);
                    const documents = getData<DocumentSearchRow>(9);
                    const tasks = getData<TaskSearchRow>(10);

                    const exactCustomerResults: SearchResult[] = [];
                    (directoryCustomers ?? []).forEach((row) => {
                        if (row.entityType === 'company' && !allowedEntitySearchTypes.has('companies')) return;
                        const display = customerSearchDisplay(row, false);
                        const result: SearchResult = row.entityType === 'company'
                            ? { id: `co-${row.id}`, type: 'company', title: display.title, subtitle: display.subtitle, icon: Building, url: resolveCrmEntityRoute('company', row.id), customerRow: row }
                            : { id: `c-${row.id}`, type: 'customer', title: display.title, subtitle: display.subtitle, icon: User, url: resolveCrmEntityRoute('customer', row.id), customerRow: row };
                        // A customer number, booking code, phone or e-mail match is what the person is looking for:
                        // it goes first so Enter opens it.
                        if (row.entityType === 'customer' && isExactCustomerMatch(row)) exactCustomerResults.push(result);
                        else newResults.push(result);
                    });
                    const partyResults = partySearchResults(parties, allowedEntitySearchTypes.has('customers'));
                    newResults.push(...partyResults.viaTraveller);
                    newResults.unshift(...exactCustomerResults, ...partyResults.exactTravellers);
                    customers.forEach((customer) => newResults.push({ id: `c-${customer.id}`, type: 'customer', title: personSearchTitle(customer, 'Unnamed customer'), subtitle: customer.email || 'Customer', icon: User, url: resolveCrmEntityRoute('customer', customer.id) }));
                    companies.forEach((company) => newResults.push({ id: `co-${company.id}`, type: 'company', title: company.company_name || 'Unnamed company', subtitle: company.relationship_type || 'Company', icon: Building, url: resolveCrmEntityRoute('company', company.id) }));
                    pnts.forEach((pnt) => newResults.push({
                        id: `p-${pnt.id}`,
                        type: 'pnt',
                        title: `Booking: ${pnt.passenger_name || 'Unknown passenger'}`,
                        subtitle: `PNR: ${pnt.pnr_locator || 'Unassigned'}`,
                        icon: Ticket,
                        url: buildMasterSearchEntityUrl('pnt', pnt.pnr_locator || pnt.passenger_name || pnt.id),
                    }));
                    invoices.forEach((invoice) => newResults.push({ id: `inv-${invoice.id}`, type: 'invoice', title: `Invoice #${invoice.invoice_number || 'Unnumbered'}`, subtitle: `${invoice.currency || ''} ${invoice.total_amount ?? ''}`.trim(), icon: FileText, url: `/invoices/${invoice.id}/builder` }));
                    suppliersData.forEach((supplier) => newResults.push({ id: `sup-${supplier.id}`, type: 'supplier', title: supplier.supplier_name || 'Unnamed supplier', subtitle: supplier.supplier_code ? `Code: ${supplier.supplier_code}` : 'Supplier', icon: Building2, url: resolveCrmEntityRoute('supplier', supplier.id) }));
                    travelers.forEach((traveler) => newResults.push({ id: `t-${traveler.id}`, type: 'traveler', title: personSearchTitle(traveler, 'Unnamed traveler'), subtitle: traveler.email || 'Traveler', icon: User, url: resolveCrmEntityRoute('traveler', traveler.id) }));
                    pnrs.forEach((pnr) => newResults.push({ id: `pnr-${pnr.id}`, type: 'pnr', title: `Locator: ${pnr.record_locator || 'Unknown'}`, subtitle: `${pnr.passenger_name || 'Unknown Pax'} • ${pnr.gds_source || 'GDS'}`, icon: FileText, url: resolvePnrRoute(pnr, pnr.id) }));
                    tickets.forEach((t) => newResults.push({
                        id: `tk-${t.id}`,
                        type: 'ticket',
                        title: `#${t.display_id} ${t.subject || '(no subject)'}`,
                        subtitle: `${freshdeskMarkLabel(t) ?? (t.status_name || 'Open')}${t.source_type === 'freshdesk_mirror' ? ` · FD #${t.freshdesk_ticket_id ?? ''}` : ' · UCC'}`,
                        icon: MessageSquare,
                        url: `/ucc/${t.display_id}`,
                    }));
                    trips.forEach((trip) => newResults.push({
                        id: `trip-${trip.id}`,
                        type: 'trip',
                        title: trip.trip_name || 'Unnamed trip',
                        subtitle: `${trip.primary_destination || 'Trip'}${trip.trip_status ? ` · ${trip.trip_status}` : ''}`,
                        icon: Plane,
                        url: buildMasterSearchEntityUrl('trip', trip.id),
                    }));
                    documents.forEach((document) => newResults.push({
                        id: `document-${document.id}`,
                        type: 'document',
                        title: document.document_name || 'Unnamed document',
                        subtitle: `${document.document_type || 'Document'}${document.document_number ? ` · ${document.document_number}` : ''}`,
                        icon: FolderOpen,
                        url: buildMasterSearchEntityUrl('document', document.id),
                    }));
                    tasks.forEach((task) => newResults.push({
                        id: `task-${task.id}`,
                        type: 'task',
                        title: task.title || 'Untitled task',
                        subtitle: `${task.status || 'Pending'}${task.priority ? ` · ${task.priority}` : ''}`,
                        icon: CheckSquare,
                        url: buildMasterSearchEntityUrl('task', task.id),
                    }));
                } catch (err) {
                    if (!controller.signal.aborted) console.error('Master search error:', err);
                }
            }

            if (!isActive) return;
            setScopedResults(dedupeSearchResults(newResults), searchTerm);
            setSelectedIndex(0);
            setIsSearching(false);
        };

        const debounce = setTimeout(search, 200);
        return () => {
            isActive = false;
            controller.abort();
            clearTimeout(debounce);
        };
    }, [allowedEntitySearchTypes, allowedGlobalActions, allowedPaths, contextCommands, navigationFeatures, query, setScopedResults]);

    const filteredResults = useMemo(() => {
        if (activeCategory === 'all') return results;

        const allowedTypes = MASTER_SEARCH_CATEGORY_TYPES[activeCategory];
        return results.filter(r => allowedTypes.includes(r.type));
    }, [results, activeCategory]);

    const groupedResults = useMemo(() => {
        const groups: Record<string, SearchResult[]> = {};
        filteredResults.forEach(result => {
            const groupKey = result.type === 'feature' ? (result.domain || 'Pages') :
                result.type === 'context' ? (result.domain || 'Context Actions') :
                result.type === 'customer' ? 'Customers' :
                result.type === 'company' ? 'Companies' :
                result.type === 'traveler' ? 'Travelers' :
                result.type === 'invoice' ? 'Invoices' :
                result.type === 'pnt' ? 'Bookings (PNT)' :
                result.type === 'pnr' ? 'PNRs' :
                result.type === 'supplier' ? 'Suppliers' :
                result.type === 'trip' ? 'Trips' :
                result.type === 'document' ? 'Documents' :
                result.type === 'task' ? 'Tasks' :
                result.type === 'ticket' ? 'UCC Tickets' : 'Other';

            if (!groups[groupKey]) groups[groupKey] = [];
            groups[groupKey].push(result);
        });
        return groups;
    }, [filteredResults]);

    // Results in the order they are shown (grouped). Arrow keys, Enter and the highlighted row all use this order,
    // so the first row on screen is the one Enter opens and the arrows never jump between groups.
    const orderedResults = useMemo(() => Object.values(groupedResults).flat(), [groupedResults]);
    // A plain Enter pressed while the results are stale (debounce pending or record search running). Ctrl/Cmd+Enter
    // is never queued: a new tab opened later, after the keypress, would be blocked or surprising.
    const pendingEnterRef = useRef<{ query: string } | null>(null);
    const typedQuery = query.trim().slice(0, 120);
    const resultsSettled = !isSearching && resultsQuery === typedQuery;

    const handleSelect = useCallback((result: SearchResult, openInNewTab = false) => {
        const permissionPaths = getResultPermissionPaths(result);
        if (!areNavigationPathsAllowed(allowedPaths, permissionPaths)) return;

        // Haptic feedback for command execution
        try { if ('vibrate' in navigator) navigator.vibrate(50); } catch { /* haptics unsupported */ }

        // Persist only a permission-checked destination label, never the raw
        // query or entity result that may contain client PII.
        if ((result.type === 'feature' || result.type === 'context') && user?.id) {
            try {
                const items = addNavigationHistoryItem(localStorage, user.id, allowedPaths, {
                    id: result.id,
                    title: result.title,
                    url: result.url,
                    permissionPaths,
                    timestamp: Date.now(),
                });
                setSearchHistory({ userId: user.id, items });
            } catch { /* localStorage unavailable — non-fatal */ }
        }
        if (openInNewTab) {
            const opened = window.open(result.url, '_blank', 'noopener,noreferrer');
            if (opened) opened.opener = null;
        } else {
            navigate(result.url);
            handleClose();
        }
    }, [allowedPaths, navigate, handleClose, user?.id]);

    // Enter pressed before the results for the typed query arrived (e.g. a customer number typed and Enter straight
    // away): open the first result once the search for that query settles. Settling with no results, or typing on,
    // drops the queued Enter.
    useEffect(() => {
        const pending = pendingEnterRef.current;
        if (!pending) return;
        if (pending.query !== typedQuery) {
            pendingEnterRef.current = null;
            return;
        }
        if (!resultsSettled) return;
        pendingEnterRef.current = null;
        const first = orderedResults[0];
        if (first) handleSelect(first, false);
    }, [handleSelect, orderedResults, resultsSettled, typedQuery]);

    useEffect(() => {
        if (!isOpen) return;
        const selected = orderedResults[selectedIndex];
        if (!selected) return;
        const element = document.getElementById(`master-search-result-${selected.id}`);
        if (element && typeof element.scrollIntoView === 'function') element.scrollIntoView({ block: 'nearest' });
    }, [isOpen, orderedResults, selectedIndex]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Edge's native password reveal shortcut must not expose a private query.
        if (privacy && e.altKey && e.key === 'F8') {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        // Support Shortwave style navigation (j/k) only if user isn't actively typing a text string
        const isCmdNav = !e.metaKey && !e.ctrlKey && e.target === inputRef.current && query.length === 0;

        if (e.key === 'ArrowDown' || (isCmdNav && e.key === 'j')) {
            e.preventDefault();
            setSelectedIndex(prev => (prev + 1) % Math.max(1, orderedResults.length));
        } else if (e.key === 'ArrowUp' || (isCmdNav && e.key === 'k')) {
            e.preventDefault();
            setSelectedIndex(prev => (prev - 1 + orderedResults.length) % Math.max(1, orderedResults.length));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const newTab = e.metaKey || e.ctrlKey;
            if (!resultsSettled) {
                // The rows on screen belong to an earlier query (or the record search is still running).
                pendingEnterRef.current = newTab ? null : { query: typedQuery };
                return;
            }
            pendingEnterRef.current = null;
            const result = orderedResults[selectedIndex] ?? orderedResults[0];
            if (result) handleSelect(result, newTab);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center px-3 pt-[min(8vh,4rem)]">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={handleClose} />
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="master-search-title"
                className="relative flex max-h-[86vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_20px_50px_rgba(0,0,0,0.5)] animate-in fade-in zoom-in-95 duration-200 dark:border-slate-800 dark:bg-slate-900"
            >
                <h2 id="master-search-title" className="sr-only">Search the Propeller Travel platform</h2>
                <div className="flex items-center px-4 py-4 border-b border-slate-100 dark:border-slate-800">
                    <Search className="w-6 h-6 text-indigo-500 mr-3 flex-shrink-0" />
                    <input
                        ref={inputRef}
                        type={privacy ? 'password' : 'text'}
                        data-private-search={privacy ? 'true' : undefined}
                        role="combobox"
                        aria-label="Search pages and records"
                        aria-describedby={privacy ? 'master-search-privacy-help' : undefined}
                        aria-expanded="true"
                        aria-controls="master-search-results"
                        aria-autocomplete="list"
                        aria-activedescendant={orderedResults[selectedIndex] ? `master-search-result-${orderedResults[selectedIndex].id}` : undefined}
                        className="flex-1 bg-transparent border-none outline-none text-xl font-medium text-slate-800 dark:text-slate-100 placeholder-slate-400"
                        placeholder="Search anything... tools, customers, PNR locators (Cmd+K)"
                        value={query}
                        onChange={(e) => {
                           setQuery(e.target.value);
                           setSelectedIndex(0);
                           // A type chip picked for the previous query must not hide what the new one finds.
                           setActiveCategory('all');
                        }}
                        onKeyDown={handleKeyDown}
                        autoComplete="off"
                        spellCheck="false"
                    />
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {isSearching && <div role="status" aria-label="Searching" className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />}
                        <kbd className="hidden sm:inline-flex h-6 items-center gap-1 rounded bg-slate-100 px-2 font-mono text-[11px] font-semibold text-slate-500 border border-slate-200">ESC</kbd>
                        <button type="button" aria-label="Close search" onClick={handleClose} className="p-1.5 hover:bg-slate-100 rounded-md transition-colors"><X className="w-5 h-5 text-slate-400" /></button>
                    </div>
                </div>

                {privacy && (
                    <p id="master-search-privacy-help" className="px-4 py-2 text-sm text-slate-500">
                        Search text is hidden in privacy mode. Result labels remain masked.
                    </p>
                )}

                {(filteredResults.length > 0 || activeCategory !== 'all') && query.length > 0 && (
                     <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 dark:border-slate-800 overflow-x-auto scroller">
                     {categoryConfig.filter(cat => cat.id === 'all' || cat.id === activeCategory || MASTER_SEARCH_CATEGORY_TYPES[cat.id].some(type => results.some(result => result.type === type))).map((cat) => {
                         const isActive = activeCategory === cat.id;
                         const CatIcon = cat.icon;
                         return (
                             <button
                                 key={cat.id}
                                 type="button"
                                 aria-pressed={isActive}
                                 onClick={() => { setActiveCategory(cat.id); setSelectedIndex(0); }}
                                 className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors whitespace-nowrap ${isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
                             >
                                 <CatIcon className="w-3.5 h-3.5" />
                                 {cat.label}
                             </button>
                         );
                     })}
                 </div>
                )}

                <div id="master-search-results" role="listbox" aria-label="Search results" className="min-h-0 flex-1 overflow-y-auto w-full p-2">
                    {filteredResults.length > 0 ? (
                        Object.entries(groupedResults).map(([groupName, groupResults]) => (
                            <div key={groupName} className="mb-2">
                                <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
                                    {groupName}
                                </div>
                                <div className="space-y-1">
                                    {groupResults.map((result) => {
                                        const globalIndex = orderedResults.indexOf(result);
                                        const isSelected = globalIndex === selectedIndex;
                                        const displayResult = result.customerRow
                                            ? customerSearchDisplay(result.customerRow, privacy)
                                            : privacy ? maskMasterSearchResult(result) : result;

                                        return (
                                            <div
                                                key={result.id}
                                                onMouseEnter={() => setSelectedIndex(globalIndex)}
                                                className={`group flex items-center rounded-xl transition-all ${isSelected ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-200 shadow-sm' : 'hover:bg-slate-50'}`}
                                            >
                                                <button
                                                    id={`master-search-result-${result.id}`}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={isSelected}
                                                    onClick={(event) => handleSelect(result, event.metaKey || event.ctrlKey)}
                                                    onFocus={() => setSelectedIndex(globalIndex)}
                                                    className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 text-left"
                                                >
                                                    <span className={`p-2.5 rounded-lg ${typeColors[result.type] || 'bg-slate-100 text-slate-500'}`}>
                                                        <result.icon className="w-4 h-4" />
                                                    </span>
                                                    <span className="flex-1 min-w-0">
                                                        <span className={`block font-semibold truncate text-[15px] ${isSelected ? 'text-indigo-900' : 'text-slate-700'}`}>
                                                            {displayResult.title}
                                                        </span>
                                                        {displayResult.subtitle && <span className="block text-[13px] text-slate-500 truncate">{displayResult.subtitle}</span>}
                                                    </span>
                                                    {isSelected && <ArrowRight aria-hidden="true" className="w-4 h-4 text-indigo-500" />}
                                                </button>
                                                <button
                                                    type="button"
                                                    aria-label={`Open ${displayResult.title} in a new tab`}
                                                    onClick={() => handleSelect(result, true)}
                                                    className="mr-2 rounded-md p-2 text-slate-400 opacity-0 transition-opacity hover:bg-white hover:text-slate-600 focus:opacity-100 group-hover:opacity-100"
                                                >
                                                    <ExternalLink aria-hidden="true" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    ) : query.trim().length > 0 && !resultsSettled ? (
                        // Still waiting for the record search (debounce or request). Saying "No matches" here was
                        // wrong: it showed before the customer search had answered.
                        <div role="status" aria-live="polite" className="py-16 text-center text-slate-500 flex flex-col items-center">
                            <div className="w-8 h-8 mb-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                            <p className="font-semibold text-lg text-slate-700">Searching…</p>
                        </div>
                    ) : query.length > 0 && activeCategory !== 'all' && results.length > 0 ? (
                        <div className="py-16 text-center text-slate-500 flex flex-col items-center">
                            <p className="font-semibold text-lg text-slate-700 mb-2">No matches of this type.</p>
                            <button
                                type="button"
                                onClick={() => { setActiveCategory('all'); setSelectedIndex(0); }}
                                className="rounded-full bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-200"
                            >
                                Show all {results.length} {results.length === 1 ? 'match' : 'matches'}
                            </button>
                        </div>
                    ) : query.length > 0 ? (
                        <div className="py-16 text-center text-slate-500 flex flex-col items-center">
                            <div className="w-16 h-16 bg-slate-100 flex items-center justify-center rounded-full mb-4">
                               <Search className="w-8 h-8 text-slate-300" />
                            </div>
                            <p className="font-semibold text-lg text-slate-700 mb-1">{privacy ? 'No matches found' : `No matches found for "${query}"`}</p>
                            <p className="text-sm">Double check the spelling or try a different locator.</p>
                        </div>
                    ) : (
                        <div className="p-2 pt-0">
                            {/* Empty state context / history */}
                            {contextCommands.length > 0 && (
                                <div className="mb-4">
                                    <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                        <Zap className="w-3.5 h-3.5" /> Context Actions
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {contextCommands.map(cmd => {
                                            const globalIndex = orderedResults.indexOf(cmd);
                                            const isSelected = globalIndex === selectedIndex;
                                            return (
                                              <button
                                                key={cmd.id}
                                                type="button"
                                                onClick={() => handleSelect(cmd)}
                                                onMouseEnter={() => setSelectedIndex(globalIndex)}
                                                onFocus={() => setSelectedIndex(globalIndex)}
                                                className={`flex w-full items-center gap-3 px-3 py-3 rounded-xl border border-slate-100 text-left shadow-sm transition-colors ${isSelected ? 'bg-indigo-50 border-indigo-100' : 'bg-white hover:bg-slate-50'}`}
                                              >
                                                <span className="p-2 rounded-lg bg-indigo-100 text-indigo-600"><cmd.icon className="w-4 h-4"/></span>
                                                <span className="text-sm font-semibold text-slate-700">{cmd.title}</span>
                                              </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            {visibleSearchHistoryItems.length > 0 && (
                                <div>
                                    <div className="flex items-center justify-between px-3 py-2">
                                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                            <History className="w-3.5 h-3.5" /> Recent
                                        </div>
                                        <button
                                            type="button"
                                            aria-label="Clear recent destinations"
                                            onClick={() => {
                                                try { clearNavigationHistory(localStorage, user?.id); } catch { /* ignore */ }
                                                setSearchHistory({ userId: user?.id ?? null, items: [] });
                                            }}
                                            className="text-[11px] font-medium text-slate-400 hover:text-red-500"
                                        >Clear</button>
                                    </div>
                                    <div className="space-y-1">
                                        {visibleSearchHistoryItems.slice(0, 5).map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                onClick={() => {
                                                    if (!areNavigationPathsAllowed(allowedPaths, item.permissionPaths)) return;
                                                    navigate(item.url);
                                                    handleClose();
                                                }}
                                                className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-slate-50"
                                            >
                                                <History className="w-3.5 h-3.5 text-slate-300" />
                                                <span className="text-[14px] font-medium text-slate-600">{item.title}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center text-[12px] font-medium text-slate-500 rounded-b-xl">
                    <div className="flex gap-4 overflow-hidden">
                        <span className="flex items-center gap-1"><kbd className="bg-white border rounded px-1.5 py-0.5 shadow-sm text-slate-600">↑↓</kbd> or <kbd className="bg-white border rounded px-1.5 py-0.5 shadow-sm text-slate-600">j</kbd> Navigate</span>
                        <span className="flex items-center gap-1"><kbd className="bg-white border rounded px-1.5 py-0.5 shadow-sm text-slate-600">Tab</kbd> Focus controls</span>
                        <span className="flex items-center gap-1"><kbd className="bg-white border rounded px-1.5 py-0.5 shadow-sm text-slate-600">↵</kbd> Select</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
